import { CustomSelectOptionBaseType } from '@danfoss/mosaic-core';
import Button, { ButtonThemes } from '../../../ui/button/Button';
import styles from './PromptHelper.module.scss';
import { Prompt, PromptSourceType } from '../models/PromptTypes';
import { Fragment, ReactNode } from 'react';
import { TourCatalogTargets } from '../../tours/hooks/useTourCatalog';
import { getClassNames } from '../../../helpers/classHelpers';
import { Icon } from '../../../ui/icon/Icon';
import { Icons } from '../../../ui/icon/icons/material';
import { IconStyles } from '../../../ui/icon/Icon.types';
import { IAdaptiveProvider } from '../../../contexts/adaptive/AdaptiveContext';
import PromptCard from '../../../ui/prompt-card/PromptCard';

const now = new Date();
export const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
export const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

export interface FilterConfig {
    createdBy?: string;
    assistantKeys?: string[];
    categories?: string[];
    searchText?: string;
}

export const filterPrompts = <T extends {
    created_by: string;
    assistant_key: string;
    category?: string;
    title?: string;
    description?: string;
}>(items: T[], config: FilterConfig): T[] => {
    if (!items?.length) return [];

    const hasCreatedByFilter = !!config.createdBy;
    const hasAssistantKeysFilter = Array.isArray(config.assistantKeys) && config.assistantKeys.length > 0;
    const hasCategoryFilter = Array.isArray(config.categories) && config.categories.length > 0;
    if (!hasCreatedByFilter && !hasAssistantKeysFilter && !hasCategoryFilter) return items;

    let filtered = [...items];

    if (hasCreatedByFilter) {
        filtered = filtered.filter(item => item.created_by === config.createdBy);
        if (!filtered.length) return [];
    }

    if (hasAssistantKeysFilter) {
        filtered = filtered.filter(item => {
            return config.assistantKeys!.includes(item.assistant_key);
        });
        if (!filtered.length) return [];
    }

    if (hasCategoryFilter) {
        filtered = filtered.filter(item => item.category && config.categories!.includes(item.category));
        if (!filtered.length) return [];
    }

    return filtered;
};

export function handleSelectChange(
    option: string | string[] | CustomSelectOptionBaseType | CustomSelectOptionBaseType[] | undefined,
    setState: (value: string[]) => void
): void {
    let values: string[] = [];

    if (typeof option === 'string') {
        values = [option];
    } else if (Array.isArray(option)) {
        values = option.map(opt =>
            typeof opt === 'string' ? opt : String(opt.value)
        );
    } else if (option && typeof option === 'object' && 'value' in option) {
        values = [String(option.value)];
    }

    setState(values);
}

export function personToggle(
    listLength: number,
    showAll: boolean,
    setShowAll: (v: boolean) => void,
    maxVisiblePrompts: number,
    seeAllLabel: string,
    seeLessLabel: string
) {
    return listLength > maxVisiblePrompts && (
        <Button
            label={showAll ? seeLessLabel : seeAllLabel}
            theme={ButtonThemes.textPrimary}
            onClick={() => setShowAll(!showAll)}
        />
    );
}

export function personasListExtras(
    listLength: number,
    showAll: boolean,
    setShowAll: (v: boolean) => void,
    maxVisiblePrompts: number,
    seeAllLabel: string,
    seeLessLabel: string
) {
    return (
        <div className={styles['personas-see-all']}>
            ({listLength})
            {personToggle(listLength, showAll, setShowAll, maxVisiblePrompts, seeAllLabel, seeLessLabel)}
        </div>
    );
}

export function container(
    headline: string,
    children: ReactNode,
    extraHeadline?: ReactNode,
    id?: string
) {
    return (
        <div className={styles.container} id={id}>
            <b>{headline} {extraHeadline}</b>
            <div className={styles['container-children']}>
                {children}
            </div>
        </div>
    );
}

type RenderCardParams = {
    onClickCard: (prompt: Prompt) => void;
    catalogFavorites: IAdaptiveProvider['catalogFavorites']; showAll: boolean;
};

export const renderCard = ({
    onClickCard,
    catalogFavorites
}: Omit<RenderCardParams, 'showAll'>) =>
    (showAll: boolean) =>
        (limit?: number, attachTourId?: boolean, sourceType: PromptSourceType = 'other') =>
            (prompt: Prompt, index: number) => {
                if (!!limit && index + 1 > limit && !showAll) return null;

                const { key } = prompt;
                const $attachTourId = attachTourId && index === 0;

                const isFavorite = sourceType === 'favorite';

                const usageCount = prompt.usage_count.toString() ?? '0';

                return (
                    <Fragment key={key}>
                        <PromptCard
                            id={$attachTourId ? TourCatalogTargets.select : undefined}
                            key={key}
                            inverseHoverEffect={true}
                            className={getClassNames([styles.card])}
                            dataAttributes={{
                                'data-count': usageCount,
                            }}
                            maxDescriptionLength={400}
                            title={prompt.title}
                            description={prompt.content}
                            chipList={(prompt.tags ?? '')
                                .split(',')
                                .map(tag => tag.trim())
                                .filter(Boolean)}
                            onClick={onClickCard.bind(null, prompt)}
                            rightSide={
                                <Icon.Async
                                    iconName={isFavorite ? Icons.star : Icons.starOutline}
                                    iconStyle={IconStyles.outlined}
                                    onAsyncClick={async (e) => {
                                        e.stopPropagation();
                                        await catalogFavorites.toggleFavoritePrompt(key);
                                    }}
                                    title=""
                                />
                            }
                        />
                    </Fragment>
                );
            };

export const getLastNDays = (days: number) => {
    const now = new Date();
    return new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
};

export function filterPromptKeysByLastUsed(
    prompts: Prompt[],
    keysToCheck: string[],
    after: Date,
    excludeKeys: Set<string> = new Set(),
    excludeKeysArr: string[] = []
): string[] {
    return keysToCheck.filter(key => {
        const prompt = prompts.find(p => p.key === key);
        return (
            prompt?.last_used &&
            new Date(prompt.last_used) > after &&
            !excludeKeys.has(key) &&
            !excludeKeysArr.includes(key)
        );
    });
}
